import React from 'react';
import PropTypes from 'prop-types';
import styles from './hotSpotInteraction.scss';
import hotSpotService from '../../../../services/questionView/HotSpotService';
import uiService from '../../../../services/UIService';

class HotSpotArea extends React.Component {
    static propTypes = {
        children: PropTypes.oneOfType([
            PropTypes.arrayOf(PropTypes.node),
            PropTypes.node,
        ]),
        model: PropTypes.object,
        hotSpot: PropTypes.object.isRequired,
        selected: PropTypes.bool,
    };

    getHotspotStyles = (shape, isSelected) => {
        const {
            borderColor,
            backgroundOpacity,
            hotspotColoringEnabled: isHotspotColorEnabled,
        } = this.props.model;
        const backgroundColorRgba = uiService.hexToRgba(borderColor);

        const getDefaultStyles = () => {
            const defaultStyles = {};

            if (isHotspotColorEnabled) {
                defaultStyles.borderColor = borderColor;
                defaultStyles.background = `rgba(14, 152, 240, ${backgroundOpacity})`;
            }

            if (backgroundColorRgba && isSelected) {
                defaultStyles.background = backgroundColorRgba;
            }

            return defaultStyles;
        };

        const getPolygonStyles = () => {
            const polygonStyles = {};

            if (isHotspotColorEnabled) {
                polygonStyles.stroke = borderColor;
                polygonStyles.strokeWidth = 2;
                polygonStyles.fill = `rgba(14, 152, 240, ${backgroundOpacity})`;
            }

            if (backgroundColorRgba && isSelected) {
                polygonStyles.fill = backgroundColorRgba;
            }

            return polygonStyles;
        };

        const shapeObj = {
            circle: getDefaultStyles,
            rect: getDefaultStyles,
            polygon: getPolygonStyles,
        };

        return shapeObj[shape]?.();
    }

    render() {
        const {
            hotSpot,
            selected,
            children,
            model: { hotspotColoringEnabled: isHotspotColorEnabled },
        } = this.props;

        const dimensions = hotSpot.coords.split(',');
        const optionalHighlightClass = selected ? ' highlight' : '';
        const optionalBackgroundClass = ' background';
        const optionalHotspotColorClass = isHotspotColorEnabled ? ' hostpotColorEnabled' : '';
        const classNames = `${styles.hotSpot}${optionalHighlightClass}${optionalBackgroundClass}${optionalHotspotColorClass}`;
        const overriddenStyles = this.getHotspotStyles(hotSpot.shape, selected);

        switch (hotSpot.shape) {
        case 'polygon':
            return (<PolygonShape
                dimensions={dimensions}
                selected={selected}
                overriddenStyles={overriddenStyles}
            >
                {children}
            </PolygonShape>);
        case 'circle':
            return (<CircleShape
                dimensions={dimensions}
                classNames={classNames}
                overriddenStyles={overriddenStyles}
            >
                {children}
            </CircleShape>);
        default:
            return (<DefaultShape
                dimensions={dimensions}
                selected={selected}
                classNames={classNames}
                overriddenStyles={overriddenStyles}
            >
                {children}
            </DefaultShape>);
        }
    }
}

const DefaultShape = ({
    dimensions,
    classNames,
    children,
    overriddenStyles,
}) => {
    const hotSpotStyle = {
        left: `${dimensions[0]}px`,
        top: `${dimensions[1]}px`,
        height: `${dimensions[3] - dimensions[1]}px`,
        width: `${dimensions[2] - dimensions[0]}px`,
    };

    return (
        <div
            className={classNames}
            data-type="hotspot"
            style={{ ...hotSpotStyle, ...overriddenStyles }}
        >
            {children}
        </div>
    );
};

DefaultShape.propTypes = {
    dimensions: PropTypes.array,
    classNames: PropTypes.string,
    overriddenStyles: PropTypes.object,
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
};

const CircleShape = ({
    dimensions,
    classNames,
    children,
    overriddenStyles,
}) => {
    const hotSpotStyle = {
        left: `${dimensions[0] - dimensions[2]}px`,
        top: `${dimensions[1] - dimensions[2]}px`,
        height: `${dimensions[2] * 2}px`,
        width: `${dimensions[2] * 2}px`,
    };

    return (
        <div
            className={`${classNames} circle`}
            data-type="hotspot"
            style={{ ...hotSpotStyle, ...overriddenStyles }}
        >
            {children}
        </div>
    );
};

CircleShape.propTypes = {
    dimensions: PropTypes.array,
    classNames: PropTypes.string,
    overriddenStyles: PropTypes.object,
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
};

const PolygonShape = ({
    dimensions,
    selected,
    children,
    overriddenStyles,
}) => {
    const result = hotSpotService.getPolygonPoints(dimensions);
    const polygonPoints = result.map(subArr => subArr.join(',')).join(' ');
    const highlight = selected ? ' selected' : '';

    const xCoords = result.map(coord => coord[0]);
    const yCoords = result.map(coord => coord[1]);

    const minX = Math.min(...xCoords);
    const maxX = Math.max(...xCoords);
    const minY = Math.min(...yCoords);
    const maxY = Math.max(...yCoords);

    const hotSpotStyle = {
        left: `${minX}px`,
        top: `${minY}px`,
        width: `${maxX - minX}px`,
        height: `${maxY - minY}px`,
    };
    const hotspotArea = {
        left: `${minX}`,
        top: `${minY}`,
        width: `${maxX - minX}`,
        height: `${maxY - minY}`,
    };

    return (
        <div className={styles.hotSpot} data-type="hotspot" style={hotSpotStyle}>
            <svg height={hotspotArea.height} width={hotspotArea.width}>
                <polygon
                    style={overriddenStyles}
                    points={polygonPoints}
                    className={`${styles.polygonShape}${highlight}`}
                    transform={`translate(${-hotspotArea.left}, ${-hotspotArea.top})`}
                />
            </svg>
            {children}
        </div>
    );
};

PolygonShape.propTypes = {
    dimensions: PropTypes.array,
    selected: PropTypes.bool,
    overriddenStyles: PropTypes.object,
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
};


export default HotSpotArea;

